import React, { useState, useRef, useEffect, useMemo } from 'react';
import '../../styles/SearchableSelect.css';

const extractEmojiOrIcon = (label, iconProp) => {
  if (iconProp) return { icon: iconProp, text: label };
  if (!label || typeof label !== 'string') return { icon: null, text: String(label || '') };
  const emojiRegex = /^(\p{Extended_Pictographic}|\p{Emoji_Presentation}|\p{Emoji}\uFE0F)\s*/u;
  const match = label.match(emojiRegex);
  if (match) {
    return { icon: match[1], text: label.slice(match[0].length).trim() };
  }
  return { icon: null, text: label };
};

const SearchableSelect = ({
  id,
  name,
  value = '',
  onChange,
  options,
  children,
  placeholder = 'Select...',
  searchPlaceholder = 'Type to search...',
  className = '',
  triggerClassName = '',
  dropdownClassName = '',
  disabled = false,
  allowCustom = false,
  required = false,
  'aria-label': ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  // Normalize options from either options prop or children
  const parsedOptions = useMemo(() => {
    if (options && Array.isArray(options)) {
      return options.map((opt) => {
        if (typeof opt === 'string' || typeof opt === 'number') {
          return { value: String(opt), label: String(opt) };
        }
        return {
          value: String(opt.value ?? ''),
          label: opt.label !== undefined ? String(opt.label) : String(opt.value ?? ''),
          icon: opt.icon,
          badge: opt.badge,
          subtext: opt.subtext,
          disabled: Boolean(opt.disabled),
        };
      });
    }

    if (children) {
      const opts = [];
      React.Children.forEach(children, (child) => {
        if (React.isValidElement(child) && child.type === 'option') {
          opts.push({
            value: String(child.props.value ?? ''),
            label: typeof child.props.children === 'string'
              ? child.props.children
              : React.Children.toArray(child.props.children).join(''),
            disabled: Boolean(child.props.disabled),
          });
        }
      });
      return opts;
    }

    return [];
  }, [options, children]);

  // Current selected option
  const selectedOption = useMemo(() => {
    return parsedOptions.find((opt) => String(opt.value) === String(value));
  }, [parsedOptions, value]);

  // Filtered options based on search query
  const filteredOptions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return parsedOptions;
    return parsedOptions.filter((opt) => {
      const labelMatch = opt.label.toLowerCase().includes(q);
      const valueMatch = opt.value.toLowerCase().includes(q);
      const subtextMatch = opt.subtext ? opt.subtext.toLowerCase().includes(q) : false;
      return labelMatch || valueMatch || subtextMatch;
    });
  }, [parsedOptions, searchQuery]);

  // Handle focus on open
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(0);
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 40);
      return () => clearTimeout(timer);
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const triggerChange = (newVal) => {
    if (!onChange) return;
    const event = {
      target: { id, name, value: newVal },
      currentTarget: { id, name, value: newVal },
      preventDefault: () => {},
      stopPropagation: () => {},
      toString: () => String(newVal),
      valueOf: () => newVal,
    };
    onChange(event, newVal);
  };

  const handleSelect = (opt) => {
    if (opt.disabled) return;
    triggerChange(opt.value);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleCustomSubmit = () => {
    if (!allowCustom || !searchQuery.trim()) return;
    triggerChange(searchQuery.trim());
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const next = prev + 1;
        return next >= filteredOptions.length ? 0 : next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const next = prev - 1;
        return next < 0 ? Math.max(0, filteredOptions.length - 1) : next;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions[highlightedIndex]) {
        handleSelect(filteredOptions[highlightedIndex]);
      } else if (allowCustom && searchQuery.trim()) {
        handleCustomSubmit();
      }
    }
  };

  const selectedDisplay = useMemo(() => {
    if (selectedOption) {
      const { icon, text } = extractEmojiOrIcon(selectedOption.label, selectedOption.icon);
      return { icon, text, badge: selectedOption.badge };
    }
    if (value !== '' && value !== undefined && value !== null) {
      return { icon: null, text: String(value) };
    }
    const emptyOption = parsedOptions.find((opt) => opt.value === '');
    if (emptyOption) {
      const { icon, text } = extractEmojiOrIcon(emptyOption.label, emptyOption.icon);
      return { icon, text, badge: emptyOption.badge };
    }
    return { icon: null, text: placeholder };
  }, [selectedOption, value, parsedOptions, placeholder]);

  return (
    <div
      ref={containerRef}
      className={`searchable-select-wrap ${className} ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''}`}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        id={id}
        className={`searchable-select-trigger ${triggerClassName}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel || placeholder}
      >
        <span className="searchable-select-value">
          {selectedDisplay.icon && (
            <span className="searchable-select-icon" aria-hidden="true">
              {selectedDisplay.icon}
            </span>
          )}
          <span className="searchable-select-text">{selectedDisplay.text}</span>
          {selectedDisplay.badge && (
            <span className="searchable-select-badge">{selectedDisplay.badge}</span>
          )}
        </span>
        <span className="searchable-select-arrow" aria-hidden="true">
          <i className="fas fa-chevron-down" />
        </span>
      </button>

      {/* Hidden input for form submission if name is provided */}
      {name && (
        <input
          type="hidden"
          name={name}
          value={value ?? ''}
          required={required}
        />
      )}

      {isOpen && (
        <div className={`searchable-select-dropdown ${dropdownClassName}`} role="listbox">
          <div className="searchable-select-search-box">
            <i className="fas fa-search searchable-select-search-icon" aria-hidden="true" />
            <input
              ref={searchInputRef}
              type="text"
              className="searchable-select-search-input"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              aria-label="Search options"
            />
            {searchQuery && (
              <button
                type="button"
                className="searchable-select-search-clear"
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchQuery('');
                  searchInputRef.current?.focus();
                }}
                aria-label="Clear search"
              >
                <i className="fas fa-times" />
              </button>
            )}
          </div>

          <div ref={listRef} className="searchable-select-options-list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt, index) => {
                const isSelected = String(opt.value) === String(value);
                const isHighlighted = index === highlightedIndex;
                const { icon, text } = extractEmojiOrIcon(opt.label, opt.icon);

                return (
                  <div
                    key={`${opt.value}-${index}`}
                    className={`searchable-select-option ${isSelected ? 'is-selected' : ''} ${isHighlighted ? 'is-highlighted' : ''} ${opt.disabled ? 'is-disabled' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(opt);
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span className="searchable-select-opt-content">
                      {icon && (
                        <span className="searchable-select-opt-icon" aria-hidden="true">
                          {icon}
                        </span>
                      )}
                      <span className="searchable-select-opt-label">{text}</span>
                      {opt.badge && (
                        <span className="searchable-select-opt-badge">{opt.badge}</span>
                      )}
                    </span>
                    {isSelected && (
                      <span className="searchable-select-check" aria-hidden="true">
                        <i className="fas fa-check" />
                      </span>
                    )}
                  </div>
                );
              })
            ) : allowCustom && searchQuery.trim() ? (
              <div
                className="searchable-select-option is-custom"
                onClick={handleCustomSubmit}
              >
                <span>
                  <i className="fas fa-plus-circle" style={{ marginRight: 6, color: '#0b4f6c' }} />
                  Use "<strong>{searchQuery.trim()}</strong>"
                </span>
              </div>
            ) : (
              <div className="searchable-select-empty">
                <i className="fas fa-inbox" style={{ marginRight: 6 }} /> No options found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
